import "./Forms.scss";

import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { type Descendant, type Editor, Node } from "slate";

import { CustomInput } from "../../../../components/CustomInput/CustomInput";
import { CustomModal } from "../../../../components/CustomModal";
import { Helpers } from "../../../../components/SlateEditor/helpers";
import { SlateEditor } from "../../../../components/SlateEditor/SlateEditor";
import type { IOption } from "../../../../components/StyledSelect/StyledSelect";
import { StyledSelect as Select } from "../../../../components/StyledSelect/StyledSelect";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import type { IApplication } from "../../../../hooks/reactQueryHooks/useApplications";
import type {
  IAddCacheItemRequest,
  ICacheItemRequest,
} from "../../../../hooks/reactQueryHooks/useCacheItems";
import { useAddCacheItem } from "../../../../hooks/reactQueryHooks/useCacheItems";
import { useCheckAccess } from "../../../../hooks/useCheckAccess";
import { isJSON } from "../../../../utils/isJSON";
import { AddCacheItemControls } from "./AddCacheItemControls";
import { CSVFileInput } from "./CSVFileInput";

interface IProps {
  applications: Array<IApplication>;
  onCancel: () => void;
}

const hasDuplicatesMessage =
  "The prompt already exists in the application. Try a different one.";

const AddCacheItemForm: React.FC<IProps> = ({ applications, onCancel }) => {
  const [upload, setUpload] = useState<boolean>(false);
  const [application, setApplication] = useState<string>("");
  const [promptValue, setPromptValue] = useState<string>("");
  const [filesValue, setFilesValue] = useState<ICacheItemRequest[] | []>([]);
  const { currentOrganization } = useOrganizationContext();
  const { mutate, reset, isError, isSuccess, isLoading, data } =
    useAddCacheItem();
  const { id: organizationId } = currentOrganization || {};
  const [editorValue, setEditorValue] = useState<Descendant[] | null>(null);
  const [submittedPromptsCount, setSubmittedPromptsCount] = useState<number>(0);

  const editorRef = createRef<Editor>();

  const handleBulkAdd = (data: ICacheItemRequest[], applicationId: string) => {
    if (organizationId) {
      const request: IAddCacheItemRequest = {
        organizationId,
        applicationId,
        cacheItems: data,
      };
      mutate(request);
    }
  };

  const handleAdd = (prompt: string, answer: string, applicationId: string) => {
    if (organizationId) {
      const request: IAddCacheItemRequest = {
        organizationId,
        applicationId,
        cacheItems: [
          {
            prompt,
            answer,
          },
        ],
      };
      mutate(request);
    }
  };

  const hasDuplicate = data && !data?.length;
  const noSubscriptionError = `Adding cache items is disabled while there is no active subscription for the ${currentOrganization?.name} organization`;
  const { displayError } = useCheckAccess(noSubscriptionError, isError);

  const applicationsOptions: IOption[] = useMemo(() => {
    return applications.map(
      app =>
        ({
          value: app.id,
          label: app.name,
        } as IOption),
    );
  }, [applications]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromptValue(e.target.value);
    if (hasDuplicate) {
      reset();
    }
  };

  const handleFilesChange = (data: ICacheItemRequest[]) => {
    setFilesValue(data);
  };

  const clearForm = () => {
    setUpload(false);
    setPromptValue("");
    setApplication("");
    setFilesValue([]);
    setSubmittedPromptsCount(0);
  };

  const handleUploadSubmit = () => {
    const values = filesValue.filter(
      item =>
        item.prompt.toLowerCase() !== "prompt" ||
        item.answer.toLowerCase() !== "answer",
    );
    setSubmittedPromptsCount(values.length);
    handleBulkAdd(values, application);
  };

  const handleSubmit = () => {
    if (editorValue) {
      const answer =
        editorRef.current && isJSON(Node.string(editorRef.current))
          ? Node.string(editorRef.current)
          : Helpers.slate2markdown(editorValue);
      handleAdd(promptValue, answer, application);
    }
  };

  const handleCancel = useCallback(() => {
    clearForm();
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    if (isSuccess && data.length) {
      handleCancel();
      toast.success(`${data.length} cache items added successfully.`);
      reset();
    }
    if (isSuccess && submittedPromptsCount > data.length) {
      handleCancel();
      toast.warning(
        `We skipped ${
          submittedPromptsCount - data?.length
        } cache items since they were duplicated.`,
      );
      reset();
    }
  }, [isSuccess, data?.length, reset, submittedPromptsCount, handleCancel]);

  const handleChangeApp = (app: IOption) => {
    if (hasDuplicate) {
      reset();
    }
    setApplication(app?.value || "");
  };

  const handleTabChange = (upload: boolean) => {
    reset();
    setUpload(upload);
  };

  return (
    <CustomModal
      show={true}
      handleClose={onCancel}
      title="Add cache item(s)"
      resizable
      footer={
        <AddCacheItemControls
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          handleUploadSubmit={handleUploadSubmit}
          upload={upload}
          isLoading={isLoading}
          uploadError={
            !filesValue.length || !application || !!displayError || isLoading
          }
          addError={
            !promptValue.trim() ||
            !editorValue ||
            !application ||
            !!displayError ||
            isLoading
          }
        />
      }
      errorMessage={
        displayError ||
        (hasDuplicate && !upload ? hasDuplicatesMessage : undefined)
      }
    >
      <p className="add-cache-description">
        Preheat the cache by adding items one at a time or add in bulk by
        uploading CSV files where each line has a prompt and answer.
      </p>
      <div className="add-cache-select-wrapper add-cache-select-application mb-7">
        <label className="d-block mt-2 fs-7 required form-label">Project</label>
        <Select
          isClearable={true}
          isDisabled={false}
          isLoading={false}
          isSearchable={false}
          options={applicationsOptions}
          onChange={app => handleChangeApp(app)}
          placeholder="Select a project"
        />
      </div>
      <div className="d-flex flex-row mb-7">
        <div className="form-check-radio d-flex flex-row align-items-center">
          <input
            type="radio"
            name="flexRadioDefault"
            id="flexRadioDefault1"
            checked={!upload}
            onChange={() => handleTabChange(false)}
          />
          <label htmlFor="flexRadioDefault1" className="form-check-label px-2">
            Single cache item
          </label>
        </div>
        <div className="d-flex mx-2 flex-row">
          <div className="form-check-radio d-flex flex-row al align-items-center px-4">
            <input
              type="radio"
              name="flexRadioDefault"
              id="flexRadioDefault2"
              checked={upload}
              onChange={() => handleTabChange(true)}
            />
            <label
              htmlFor="flexRadioDefault2"
              className="form-check-label px-2"
            >
              CSV upload (FAQ file)
            </label>
          </div>
        </div>
      </div>
      {!upload ? (
        <>
          <CustomInput
            label="Prompt"
            handleChange={handlePromptChange}
            inputValue={promptValue}
            placeholder="Enter the prompt here..."
            required={true}
            className="mb-0"
          />
          <SlateEditor
            isLoading={false}
            defaultValue={[{ type: "paragraph", children: [{ text: "" }] }]}
            accessAllowed={true}
            setValue={setEditorValue}
            placeholder="Enter the answer here..."
            inPopup
            ref={editorRef}
          />
        </>
      ) : (
        <CSVFileInput
          onFileChange={handleFilesChange}
          maxFileSize={1677721}
          maxStrokeLength={1000}
        />
      )}
    </CustomModal>
  );
};

export { AddCacheItemForm };
